import { useEffect, useState, type FormEvent } from "react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { ForbiddenPanel } from "../../components/admin/ForbiddenPanel";
import { authedFetch } from "../../lib/api-client";
import { useT } from "../../lib/use-t";
import type { CachePurgeResponse } from "../../lib/admin/types";

type RoleState = "loading" | "ok" | "forbidden" | "error";

type CachedFile = {
  id: string;
  path: string;
  bytes: number;
  exists: boolean;
};

type CachedRow = {
  questionId: string;
  sessionId: string;
  userIdentifier: string;
  deelLabel: string;
  promptText: string;
  generatorProvider: string | null;
  images: CachedFile[];
  audio: CachedFile | null;
};

type ListState =
  | { status: "loading" }
  | { status: "ok"; rows: CachedRow[] }
  | { status: "error"; message: string };

type PurgeStatus =
  | { status: "idle" }
  | { status: "pending"; questionId: string }
  | { status: "ok"; questionId: string; result: CachePurgeResponse }
  | { status: "error"; questionId: string; message: string };

function totalBytes(row: CachedRow): number {
  const img = row.images.reduce((s, f) => s + (f.exists ? f.bytes : 0), 0);
  const aud = row.audio?.exists ? row.audio.bytes : 0;
  return img + aud;
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export function AdminCachePage() {
  const t = useT();
  const [roleState, setRoleState] = useState<RoleState>("loading");
  const [list, setList] = useState<ListState>({ status: "loading" });
  const [purge, setPurge] = useState<PurgeStatus>({ status: "idle" });
  const [manualId, setManualId] = useState("");

  useEffect(() => {
    let cancelled = false;
    authedFetch("/admin/users")
      .then((r) => {
        if (cancelled) return;
        if (r.status === 403) setRoleState("forbidden");
        else if (r.ok) setRoleState("ok");
        else setRoleState("error");
      })
      .catch(() => {
        if (!cancelled) setRoleState("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (roleState !== "ok") return;
    let cancelled = false;
    setList({ status: "loading" });
    authedFetch("/admin/cache/list")
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (cancelled) return;
        if (!r.ok || !body?.ok) {
          setList({
            status: "error",
            message: body?.error?.message ?? `Server returned ${r.status}`,
          });
          return;
        }
        setList({ status: "ok", rows: body.data.items as CachedRow[] });
      })
      .catch((err) => {
        if (cancelled) return;
        setList({
          status: "error",
          message: err instanceof Error ? err.message : t("chrome.common.unknownError"),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [roleState, purge.status === "ok" ? purge.questionId : ""]);

  async function purgeQuestion(questionId: string) {
    const id = questionId.trim();
    if (!id) return;
    setPurge({ status: "pending", questionId: id });
    try {
      const r = await authedFetch("/admin/cache/purge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ questionId: id }),
      });
      const body = await r.json();
      if (!body.ok) {
        setPurge({
          status: "error",
          questionId: id,
          message: body.error?.message ?? `Server returned ${r.status}`,
        });
        return;
      }
      setPurge({ status: "ok", questionId: id, result: body.data });
      if (manualId === id) setManualId("");
    } catch (err) {
      setPurge({
        status: "error",
        questionId: id,
        message: err instanceof Error ? err.message : t("chrome.common.unknownError"),
      });
    }
  }

  function onManualSubmit(ev: FormEvent) {
    ev.preventDefault();
    purgeQuestion(manualId);
  }

  return (
    <AdminLayout>
      {roleState === "loading" ? (
        <p className="text-sm text-[var(--ink-faint)]">{t("chrome.common.loadingEllipsis")}</p>
      ) : roleState === "forbidden" ? (
        <ForbiddenPanel />
      ) : (
        <div className="space-y-8">
          <header className="space-y-2">
            <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
              {t("admin.cache.eyebrow")}
            </p>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              {t("admin.cache.title")}
            </h1>
            <p className="text-sm text-[var(--ink-muted)] leading-relaxed max-w-2xl">
              {t("admin.cache.description")}
            </p>
          </header>

          {list.status === "loading" ? (
            <p className="text-sm text-[var(--ink-faint)]">{t("admin.cache.listLoading")}</p>
          ) : list.status === "error" ? (
            <p
              role="alert"
              className="rounded-md border border-[var(--fail)] bg-[var(--fail)]/10 px-3 py-2 text-sm text-[var(--fail)]"
            >
              {list.message}
            </p>
          ) : (
            <CachedTable
              rows={list.rows}
              purge={purge}
              onPurge={purgeQuestion}
            />
          )}

          <section aria-labelledby="purge-form" className="space-y-4">
            <h2
              id="purge-form"
              className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge"
            >
              {t("admin.cache.purgeByIdHeading")}
            </h2>
            <form onSubmit={onManualSubmit} className="flex items-end gap-3">
              <label className="block flex-1 max-w-md">
                <span className="block text-xs font-mono-badge uppercase tracking-wider text-[var(--ink-faint)] mb-1">
                  {t("admin.cache.questionIdLabel")}
                </span>
                <input
                  type="text"
                  value={manualId}
                  onChange={(e) => setManualId(e.target.value)}
                  spellCheck={false}
                  autoComplete="off"
                  className="w-full rounded-md border border-[var(--border)] bg-[var(--panel-soft)] px-3 py-2 text-sm font-mono-badge text-[var(--ink)] focus:border-[var(--accent)] focus:outline-2 focus:outline-[var(--accent)] focus:outline-offset-2"
                  placeholder={t("admin.cache.questionIdPlaceholder")}
                />
              </label>
              <button
                type="submit"
                disabled={
                  (purge.status === "pending" && purge.questionId === manualId) ||
                  !manualId.trim()
                }
                className="text-xs font-mono-badge text-[var(--ink-muted)] border border-[var(--border)] rounded-md px-3 py-2 hover:border-[var(--accent)] hover:text-[var(--ink)] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {purge.status === "pending" && purge.questionId === manualId
                  ? t("admin.cache.purgeBusy")
                  : `↻ ${t("admin.cache.purgeButton")}`}
              </button>
            </form>
          </section>
        </div>
      )}
    </AdminLayout>
  );
}

function CachedTable({
  rows,
  purge,
  onPurge,
}: {
  rows: CachedRow[];
  purge: PurgeStatus;
  onPurge: (id: string) => void;
}) {
  const t = useT();
  if (rows.length === 0) {
    return (
      <p className="text-sm text-[var(--ink-faint)] font-serif-italic">
        {t("admin.cache.emptyCache")}
      </p>
    );
  }
  return (
    <section aria-labelledby="cached-list" className="space-y-3">
      <h2
        id="cached-list"
        className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge"
      >
        {t("admin.cache.cacheSummaryFmt", { count: rows.length, size: formatBytes(rows.reduce((s, r) => s + totalBytes(r), 0)) })}
      </h2>
      <div className="overflow-x-auto rounded-lg border border-[var(--border)] bg-[var(--panel)]">
        <table className="w-full text-sm">
          <thead className="text-xs font-mono-badge uppercase tracking-wider text-[var(--ink-faint)] border-b border-[var(--border)]">
            <tr>
              <th className="text-left px-3 py-2">{t("admin.cache.tableColPart")}</th>
              <th className="text-left px-3 py-2">{t("admin.cache.tableColUser")}</th>
              <th className="text-left px-3 py-2">{t("admin.cache.tableColQuestion")}</th>
              <th className="text-left px-3 py-2">{t("admin.cache.tableColFiles")}</th>
              <th className="text-right px-3 py-2">{t("admin.cache.tableColSize")}</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isPending = purge.status === "pending" && purge.questionId === r.questionId;
              const recent =
                purge.status !== "idle" && purge.questionId === r.questionId ? purge : null;
              return (
                <tr
                  key={r.questionId}
                  className="border-b border-[var(--border)]/60 last:border-b-0 hover:bg-[var(--panel-soft)]/40"
                >
                  <td className="px-3 py-2 font-mono-badge text-xs text-[var(--ink-muted)]">
                    {r.deelLabel}
                  </td>
                  <td className="px-3 py-2 text-xs text-[var(--ink-muted)]">
                    {r.userIdentifier}
                  </td>
                  <td className="px-3 py-2 max-w-md truncate font-serif-italic text-[var(--ink)]">
                    {r.promptText}
                  </td>
                  <td className="px-3 py-2 font-mono-badge text-xs text-[var(--ink-muted)] tabular-nums">
                    {r.images.filter((f) => f.exists).length} img
                    {r.audio?.exists ? " + audio" : ""}
                  </td>
                  <td className="px-3 py-2 text-right font-mono-badge text-xs text-[var(--ink-muted)] tabular-nums">
                    {formatBytes(totalBytes(r))}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => onPurge(r.questionId)}
                      disabled={isPending}
                      className="text-xs font-mono-badge text-[var(--ink-muted)] border border-[var(--border)] rounded-md px-2 py-1 hover:border-[var(--accent)] hover:text-[var(--ink)] disabled:opacity-60"
                      aria-label={t("admin.cache.purgeRowAriaLabelFmt", { id: r.questionId })}
                    >
                      {isPending ? t("admin.cache.purgeBusy") : recent?.status === "ok" ? "✓" : `↻ ${t("admin.cache.purgeButton")}`}
                    </button>
                    {recent?.status === "error" ? (
                      <p
                        role="alert"
                        className="mt-1 text-[10px] text-[var(--fail)] font-mono-badge"
                      >
                        {recent.message}
                      </p>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
